(() => {
  'use strict';

  const isMobile = () => window.matchMedia('(max-width: 767px)').matches;

  // Set VH once (avoid multiple listeners if script executes more than once)
  (function setVhOnce() {
    if (window.__socialProofsVhInit) return;
    window.__socialProofsVhInit = true;
    const set = () => document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    set();
    window.addEventListener('resize', set);
  })();

  function whenVisible(el, cb, thresholdPx) {
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      cb();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            cb();
          }
        });
      },
      { rootMargin: `${thresholdPx || 500}px 0px` }
    );
    observer.observe(el);
  }

  function safePlay(video) {
    if (!video) return;
    const p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {});
    }
  }

  function primeVideoForDownload(video, preloadValue) {
    if (!video || video.dataset.spPrimed === '1') return;
    video.dataset.spPrimed = '1';

    // If the <video> has no <source>, inject it only when we actually want to load.
    // (Keeps initial HTML lightweight and prevents early MP4 fetches.)
    try {
      const hasSource = !!video.querySelector('source[src]');
      const src = video.dataset && video.dataset.src;
      if (!hasSource && src) {
        const source = document.createElement('source');
        source.src = src;
        source.type = 'video/mp4';
        video.appendChild(source);
      }
    } catch (e) {}

    // Upgrade preload once the section is visible so all slider videos start downloading.
    // This keeps initial page load fast while ensuring smooth slider UX.
    try {
      video.preload = preloadValue || 'auto';
    } catch (e) {}
    try {
      video.load();
    } catch (e) {}
  }

  function initSection(sectionRoot) {
    if (!sectionRoot || sectionRoot.dataset.socialProofsInit === 'true') return;
    sectionRoot.dataset.socialProofsInit = 'true';

    let isDragging = false;

    /** Flickity (inline slider) */
    const sliderContainer = sectionRoot.querySelector('.proofs-video-container');
    const pauseAllInlineVideos = () => {
      sliderContainer?.querySelectorAll('video').forEach((v) => v.pause());
    };
    let flkty = null;
    if (sliderContainer && window.Flickity) {
      const mobileAutoscrollEnabled = sliderContainer.dataset.mobileAutoscroll === 'true';
      const mobileAutoscrollSpeed = parseInt(sliderContainer.dataset.mobileAutoscrollSpeed || '0', 10);
      const mobileAutoplay =
        isMobile() && mobileAutoscrollEnabled && Number.isFinite(mobileAutoscrollSpeed) && mobileAutoscrollSpeed > 0
          ? mobileAutoscrollSpeed
          : false;

      flkty = new Flickity(sliderContainer, {
        cellAlign: 'left',
        contain: true,
        wrapAround: true,
        prevNextButtons: false,
        imagesLoaded: true,
        adaptiveHeight: true,
        pageDots: false,
        autoPlay: mobileAutoplay
      });

      flkty.on('dragStart', () => (isDragging = true));
      flkty.on('dragEnd', () => setTimeout(() => (isDragging = false), 50));

      sectionRoot.querySelector('.flickity-custom-proofs-prev')?.addEventListener('click', () => flkty.previous());
      sectionRoot.querySelector('.flickity-custom-proofs-next')?.addEventListener('click', () => flkty.next());

      // Autoplay the inline videos that are visible in the viewport.
      // Desktop can show multiple tiles at once; limit concurrency to avoid decoding too many videos.
      const inlineVideos = Array.from(sliderContainer.querySelectorAll('video'));
      const visibilityByVideo = new Map();

      const maxInlineAutoplay = window.matchMedia('(min-width: 768px)').matches ? 4 : 1;

      const playVisibleInlineVideos = () => {
        if (!inlineVideos.length) return;

        const visible = inlineVideos
          .map((v) => ({ v, r: visibilityByVideo.get(v) || 0 }))
          .filter(({ r }) => r >= 0.25)
          .sort((a, b) => b.r - a.r)
          .slice(0, maxInlineAutoplay)
          .map(({ v }) => v);

        // If none are meaningfully visible, pause all to save CPU.
        if (!visible.length) {
          pauseAllInlineVideos();
          return;
        }

        inlineVideos.forEach((v) => {
          if (!visible.includes(v)) v.pause();
        });

        visible.forEach((v) => {
          primeVideoForDownload(v, 'auto');
          v.muted = true;
          if (v.paused) safePlay(v);
        });
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              visibilityByVideo.set(entry.target, entry.intersectionRatio);
            });
            // batch updates to next frame to avoid layout jitter
            requestAnimationFrame(playVisibleInlineVideos);
          },
          { threshold: [0, 0.25, 0.5, 0.75, 1] }
        );
        inlineVideos.forEach((v) => io.observe(v));

        // When Flickity moves slides, re-evaluate after settle.
        flkty.on('settle', () => requestAnimationFrame(playVisibleInlineVideos));

        // Initial evaluation.
        requestAnimationFrame(playVisibleInlineVideos);
      } else {
        // Fallback: play the currently selected slide's video.
        const selected = flkty?.selectedElement;
        const v = selected?.querySelector('video') || inlineVideos[0];
        if (v) {
          primeVideoForDownload(v, 'auto');
          v.muted = true;
          safePlay(v);
        }
      }
    }

    /** Swiper (popup slider) */
    const sliderPopupContainer = sectionRoot.querySelector('.social-proof-popup-video-container');
    const videoPopupContainer = sectionRoot.querySelector('.video-play-popup-content');
    const popupContainer = sectionRoot.querySelector('.video-play-popup-container');
    const popupClose = sectionRoot.querySelector('.video-sl-popup__close');
    const playBtns = sectionRoot.querySelectorAll('.js-play-proofs-btn');

    if (!sliderPopupContainer || !window.Swiper) return;

    const nextEl = sliderPopupContainer.querySelector('.swiper-button-next-custom');
    const prevEl = sliderPopupContainer.querySelector('.swiper-button-prev-custom');

    const popupSwiper = new Swiper(sliderPopupContainer, {
      slidesPerView: 1,
      direction: 'vertical',
      speed: 500,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl
      }
    });

    const slides = sliderPopupContainer.querySelectorAll('.swiper-slide');
    const soundEnableBtn = videoPopupContainer?.querySelector('.video-sl-slider-popup-mute');
    const soundDisableBtn = videoPopupContainer?.querySelector('.video-sl-slider-popup-sound');
    let isMuted = true; // start muted for autoplay friendliness

    function ensurePopupVideo(slide) {
      if (!slide) return null;
      const existing = slide.querySelector('video');
      if (existing) return existing;

      const url = slide.getAttribute('data-video-url');
      if (!url) return null;
      const poster = slide.getAttribute('data-video-poster');

      const slot = slide.querySelector('.video-sl-popup__video-slot') || slide;

      const video = document.createElement('video');
      video.setAttribute('playsinline', '');
      video.muted = isMuted;
      video.autoplay = false;
      video.controls = false;
      video.preload = 'auto';
      if (poster) video.poster = poster;

      // Keep parity with previous liquid output (no controls, click-to-play).
      const source = document.createElement('source');
      source.src = url;
      source.type = 'video/mp4';
      video.appendChild(source);

      slot.appendChild(video);

      // Progress bar updates (attach directly; media events may not bubble reliably).
      const progressBar = slide.querySelector('.video-sl-popup__slide__progress');
      video.addEventListener('timeupdate', () => {
        if (!progressBar || !video.duration) return;
        const percent = (video.currentTime / video.duration) * 100;
        progressBar.style.width = `${percent}%`;
      });

      return video;
    }

    function unloadPopupVideo(slide) {
      const video = slide?.querySelector('video');
      if (!video) return;
      try {
        video.pause();
      } catch (e) {}
      // Remove the element to free memory; it will be recreated on demand.
      try {
        video.remove();
      } catch (e) {}
    }

    const setGlobalMute = (mute) => {
      slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (video) video.muted = mute;
      });
      isMuted = mute;
    };

    const setMuteUi = (mute) => {
      setGlobalMute(mute);
      if (soundDisableBtn && soundEnableBtn) {
        // mute=true => show mute icon, hide sound icon
        soundEnableBtn.style.display = mute ? 'block' : 'none';
        soundDisableBtn.style.display = mute ? 'none' : 'block';
      }
    };

    if (soundDisableBtn && soundEnableBtn) {
      // Start muted UI state
      setMuteUi(true);

      soundDisableBtn.addEventListener('click', () => {
        setMuteUi(true);
      });

      soundEnableBtn.addEventListener('click', () => {
        setMuteUi(false);
      });
    }

    // Delegate popup play/pause to support on-demand video creation.
    slides.forEach((slide) => {
      slide.addEventListener('click', (e) => {
        const playBtn = slide.querySelector('.popup-play-btn');
        const clickedPlayBtn = e.target && e.target.closest ? e.target.closest('.popup-play-btn') : null;

        // Clicking on the button (or video) toggles play/pause for that slide's video
        if (!clickedPlayBtn && e.target && e.target.tagName !== 'VIDEO') return;

        const video = ensurePopupVideo(slide);
        if (!video) return;

        if (video.paused) {
          safePlay(video);
          if (playBtn) playBtn.style.display = 'none';
        } else {
          video.pause();
          if (playBtn) playBtn.style.display = 'block';
        }
      });
    });

    function playActivePopupSlide(activeIndex) {
      slides.forEach((slide, idx) => {
        const video = idx === activeIndex ? ensurePopupVideo(slide) : slide.querySelector('video');
        const progressBar = slide.querySelector('.video-sl-popup__slide__progress');
        const playBtn = slide.querySelector('.popup-play-btn');

        if (playBtn) playBtn.style.display = 'none';

        if (idx === activeIndex) {
          if (!video) return;
          video.muted = isMuted;
          video.currentTime = 0;
          safePlay(video);
        } else {
          if (video) video.pause();
          if (progressBar) progressBar.style.width = '0%';
        }
      });

      // Keep only active + neighbors mounted to reduce memory/CPU
      const keep = new Set([activeIndex, activeIndex - 1, activeIndex + 1]);
      slides.forEach((slide, idx) => {
        if (!keep.has(idx)) unloadPopupVideo(slide);
      });
    }

    popupSwiper.on('slideChange', function () {
      playActivePopupSlide(this.activeIndex);
    });

    playBtns.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        if (isDragging) return;
        pauseAllInlineVideos();
        popupContainer?.classList.add('m-show');
        popupSwiper.slideTo(index, 500);
        playActivePopupSlide(index);
      });
    });

    // "Sound disabled" CTA on inline tiles: open popup AND enable sound
    const soundCtas = sectionRoot.querySelectorAll('.js-proofs-sound-open');
    soundCtas.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isDragging) return;
        const idxRaw = btn.getAttribute('data-proof-index');
        const idx = idxRaw ? parseInt(idxRaw, 10) : 0;
        popupContainer?.classList.add('m-show');
        // enable sound before starting playback
        setMuteUi(false);
        popupSwiper.slideTo(idx, 500);
        playActivePopupSlide(idx);
      });
    });

    popupClose?.addEventListener('click', () => {
      popupContainer?.classList.remove('m-show');
      slides.forEach((slide) => unloadPopupVideo(slide));
    });

    const buyVideoBtns = sectionRoot.querySelectorAll('.buy-now-from-video');
    buyVideoBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const originalAtc = document.querySelector('[data-add-to-cart]');
        if (originalAtc) originalAtc.click();
        if (popupClose) popupClose.click();
      });
    });
  }

  function initAll() {
    const sections = document.querySelectorAll('[data-social-proofs-section]');
    if (!sections.length) return;

    sections.forEach((sectionRoot) => {
      // Only init/load videos when the section is actually in viewport
      // (no preloading 600px before; requested behavior).
      whenVisible(sectionRoot, () => initSection(sectionRoot), 0);
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('page:loaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
